import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import axios from "axios";

import newlogo from "../assets/images/new_logo.png";
import { SvgIcons } from "./SvgIcons";
import { PRODUCTS, PROD_HUES, PRODUCT_ICONS, BLOG, FEATURED } from "../data/products";

// same slug BlogDetail resolves posts by
const slugify = (title = "") =>
  title
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const readMins = (html = "") =>
  Math.max(1, Math.round(html.replace(/<[^>]*>/g, " ").trim().split(/\s+/).filter(Boolean).length / 200));

export default function Navbar() {
  const location = useLocation();

  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProductOpen, setMobileProductOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [posts, setPosts] = useState([]);

  // Pull the real blog list so the mega-menu links to actual articles (not placeholder anchors)
  useEffect(() => {
    axios
      .get("https://macowebapi.macomal.com/api/macotech/blog")
      .then((r) => setPosts(Array.isArray(r.data?.Data) ? r.data.Data : []))
      .catch(() => {});
  }, []);

  // Featured = latest article; "From the blog" = next three. Fall back to static copy until loaded.
  const feat = posts[0]
    ? { to: `/blog/${encodeURIComponent(slugify(posts[0].Title))}`, title: posts[0].Title }
    : { to: "/blog", title: FEATURED.title };

  const blogItems = posts.length
    ? posts.slice(1, 4).map((p) => ({
        key: p.BlogId,
        to: `/blog/${encodeURIComponent(slugify(p.Title))}`,
        cat: p.Category || "Playbook",
        read: `${readMins(p.Content)} min`,
        title: p.Title,
      }))
    : BLOG.map((b) => ({ key: b.slug, to: "/blog", cat: b.cat, read: b.read, title: b.title }));

  // Shrink + restyle the floating nav past 10px of scroll (matches design site.js)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Mega-menu hover intent: keep the panel open while the cursor crosses the
  // gap between the trigger and the panel (matches design's 150ms close delay).
  const megaTimer = useRef(null);
  const openMega = () => {
    if (window.innerWidth <= 768) return;
    clearTimeout(megaTimer.current);
    setMegaOpen(true);
  };
  const closeMega = () => {
    if (window.innerWidth <= 768) return;
    clearTimeout(megaTimer.current);
    megaTimer.current = setTimeout(() => setMegaOpen(false), 180);
  };
  useEffect(() => () => clearTimeout(megaTimer.current), []);

  const closeMenus = () => {
    clearTimeout(megaTimer.current);
    setMegaOpen(false);
    setMobileOpen(false);
  };

  useEffect(() => {
    document.body.classList.toggle("menu-open", mobileOpen);

    return () => {
      document.body.classList.remove("menu-open");
    };
  }, [mobileOpen]);

  return (
    <>
      <header
        className={`nav ${scrolled ? "scrolled" : ""} ${megaOpen ? "mega-open" : ""} ${mobileOpen ? "mobile-open" : ""
          }`}
      >
        <div className="wrap nav-inner">
          <Link to="/" className="brand">
            <img src={newlogo} alt="logo" />
          </Link>

          <nav className="nav-links">
            <Link
              to="/"
              className={
                location.pathname === "/" ? "active" : ""
              }
            >
              Home
            </Link>

            <div
              className="nav-item"
              data-mega
              onMouseEnter={openMega}
              onMouseLeave={closeMega}
            >
              <Link
                to="/product"
                className="mega-toggle"
                onClick={(e) => {
                  if (window.innerWidth <= 768) {
                    e.preventDefault();
                    setMegaOpen(!megaOpen);
                  }
                }}
              >
                Products
                <IoIosArrowDown className="mega-caret" />
              </Link>
            </div>

            <Link
              to="/blog"
              className={
                location.pathname === "/blog" ? "active" : ""
              }
            >
              Blog
            </Link>

            <Link
              to="/about"
              className={
                location.pathname === "/about" ? "active" : ""
              }
            >
              About
            </Link>

            <Link
              to="/contact"
              className={
                location.pathname === "/contact"
                  ? "active"
                  : ""
              }
            >
              Contact
            </Link>
          </nav>

          <div className="nav-cta">
            <Link
              to="/contact"
              className="btn btn-primary"
            >
              Book a demo
            </Link>
          </div>

          <button
            className="nav-burger"
            onClick={() => setMobileOpen((prev) => !prev)}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: SvgIcons({ name: mobileOpen ? "close" : "menu"}),
              }}
            />
          </button>
        </div>

        {/* MEGA MENU */}
        {megaOpen && (
          <div
            className="nav-mega"
            onMouseEnter={openMega}
            onMouseLeave={closeMega}
          >
            <div className="wrap nav-mega-inner">
              <div className="mega-col mega-col-products">
                <span className="mega-label">
                  All products
                </span>

                <div className="mega-grid">
                  {PRODUCTS.map((p, i) => (
                    <Link
                      key={p.slug}
                      className="mega-prod"
                      to={`${p.url}`}
                      style={{
                        "--pc":
                          PROD_HUES[
                          i % PROD_HUES.length
                          ],
                      }}
                      onClick={closeMenus}
                    >
                      <span className="mega-ic" dangerouslySetInnerHTML={{
                        __html: SvgIcons({
                          name: PRODUCT_ICONS[p.slug] ||
                            "box"
                        })
                      }} />

                      <span className="mega-prod-txt">
                        <span className="mega-prod-name">
                          {p.name}
                        </span>

                        <span className="mega-prod-tag">
                          {p.tag}
                        </span>
                      </span>

                      <span className="mega-prod-arr" dangerouslySetInnerHTML={{ __html: SvgIcons({ name: "arrow" }) }} />
                    </Link>
                  ))}
                </div>

                <Link
                  className="mega-viewall"
                  to="/product"
                  onClick={closeMenus}
                >
                  Explore the full suite
                  <span dangerouslySetInnerHTML={{ __html: SvgIcons({ name: "arrow" }) }} />
                </Link>
              </div>

              <div className="mega-col mega-col-blog">
                <span className="mega-label">
                  From the blog
                </span>

                <div className="mega-posts">
                  {blogItems.map((b) => (
                    <Link
                      key={b.key}
                      className="mega-post"
                      to={b.to}
                      onClick={closeMenus}
                    >
                      <span className="mega-post-meta">
                        {b.cat} · {b.read} read
                      </span>

                      <span className="mega-post-title">
                        {b.title}
                      </span>
                    </Link>
                  ))}
                </div>

                <Link
                  className="mega-viewall"
                  to="/blog"
                  onClick={closeMenus}
                >
                  Visit the blog
                  <span dangerouslySetInnerHTML={{ __html: SvgIcons({ name: "arrow" }) }} />
                </Link>
              </div>

              <Link
                className="mega-feature"
                to={feat.to}
                onClick={closeMenus}
              >
                <span className="mega-feature-ic" dangerouslySetInnerHTML={{ __html: SvgIcons({ name: "book" }) }} />

                <span className="mega-feature-foot">
                  <span className="mega-feature-kicker">
                    Featured guide
                  </span>

                  <span className="mega-feature-title">
                    {feat.title}
                  </span>

                  <span className="mega-feature-cta">
                    Read the guide
                    <span dangerouslySetInnerHTML={{ __html: SvgIcons({ name: "arrow" }) }} />
                  </span>
                </span>
              </Link>
            </div>
          </div>
        )}
      </header>

      <div className="mobile-menu" style={{ display: mobileOpen ? "block" : "none" }}>
  <Link to="/" onClick={closeMenus}>
    Home
  </Link>

  {/* Products Menu */}
  <div className="mobile-submenu">
    <button
      type="button"
      className="mobile-submenu-btn"
      onClick={() => setMobileProductOpen((prev) => !prev)}
    >
      Products
      <IoIosArrowDown
        className={mobileProductOpen ? "rotate" : ""}
      />
    </button>

    {mobileProductOpen && (
      <div className="mobile-submenu-list">
        {PRODUCTS.map((product) => (
          <Link
            key={product.slug}
            to={`/product#${product.slug}`}
            onClick={closeMenus}
          >
            {product.name}
          </Link>
        ))}
      </div>
    )}
  </div>

  <Link to="/blog" onClick={closeMenus}>
    Blog
  </Link>

  <Link to="/about" onClick={closeMenus}>
    About
  </Link>

  <Link to="/contact" onClick={closeMenus}>
    Contact
  </Link>

  <Link
    to="/contact"
    className="btn btn-primary"
    onClick={closeMenus}
  >
    Book a demo
  </Link>
</div>
    </>
  );
}